/**
 * Receive-side picture degradation. One WebGL program, one quad, one uniform that matters: q (0..1).
 * Scanlines, grain, chroma bleed, sync roll, tearing and a signal-lost slate — all derived from q.
 * Used for live webcam feeds and for the procedural portrait alike: one visual language for every
 * unreliable eye on the ship.
 */
const VS = `attribute vec2 p; varying vec2 uv; void main(){ uv = p*0.5+0.5; uv.y = 1.0-uv.y; gl_Position = vec4(p,0.0,1.0); }`;
const FS = `precision mediump float;
varying vec2 uv; uniform sampler2D tex; uniform float q; uniform float t; uniform float tint;
float hash(vec2 v){ return fract(sin(dot(v, vec2(12.9898,78.233)))*43758.5453); }
void main(){
  float bad = 1.0 - q;
  vec2 p = uv;
  // barrel curvature
  vec2 c = p*2.0-1.0; c *= 1.0 + 0.08*dot(c,c); p = c*0.5+0.5;
  // sync roll: the picture slides vertically in bursts as quality drops
  float roll = step(0.75, hash(vec2(floor(t*2.0), 1.0))) * bad * fract(t*0.8);
  p.y = fract(p.y + roll);
  // horizontal tearing on random lines
  float line = floor(p.y*60.0);
  float tear = (hash(vec2(line, floor(t*12.0))) - 0.5) * bad * bad * 0.25;
  p.x += tear;
  // chroma bleed: sample channels slightly apart
  float bleed = bad*0.012;
  vec3 col = vec3(texture2D(tex, p+vec2(bleed,0.0)).r, texture2D(tex, p).g, texture2D(tex, p-vec2(bleed,0.0)).b);
  float lum = dot(col, vec3(0.299,0.587,0.114));
  // near-grayscale, phosphor tint (0 = bluish security cam, 1 = green phosphor)
  vec3 phosphor = mix(vec3(0.78,0.86,1.0), vec3(0.6,1.0,0.65), tint);
  vec3 outc = mix(vec3(lum), col, 0.15) * phosphor;
  // scanlines + grain
  float scan = 0.85 + 0.15*sin(p.y*480.0);
  float grain = (hash(p*vec2(640.0,480.0) + t) - 0.5) * (0.08 + bad*0.9);
  outc = outc*scan + grain;
  // vignette + edge darkening
  float vig = smoothstep(1.35, 0.4, length(c));
  outc *= vig;
  // outside the curved picture → black
  if (p.x<0.0||p.x>1.0||p.y<0.0||p.y>1.0) outc = vec3(0.0);
  // signal lost: pure static
  if (q < 0.12) outc = vec3(hash(uv*vec2(320.0,240.0)+t)) * 0.6 * phosphor;
  gl_FragColor = vec4(outc, 1.0);
}`;

export interface FeedSource { element: HTMLVideoElement | HTMLCanvasElement; quality: number; tint: number }

export class FeedRenderer {
  private gl: WebGLRenderingContext;
  private tex: WebGLTexture;
  private uQ: WebGLUniformLocation; private uT: WebGLUniformLocation; private uTint: WebGLUniformLocation;
  private raf = 0;
  private t0 = performance.now();
  source: FeedSource | null = null;

  constructor(readonly canvas: HTMLCanvasElement) {
    const gl = canvas.getContext("webgl", { antialias: false, alpha: false, preserveDrawingBuffer: true });
    if (!gl) throw new Error("webgl unavailable");
    this.gl = gl;
    const prog = gl.createProgram()!;
    for (const [type, src] of [[gl.VERTEX_SHADER, VS], [gl.FRAGMENT_SHADER, FS]] as const) {
      const sh = gl.createShader(type)!; gl.shaderSource(sh, src); gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(sh) ?? "shader");
      gl.attachShader(prog, sh);
    }
    gl.linkProgram(prog); gl.useProgram(prog);
    const buf = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p"); gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    this.tex = gl.createTexture()!; gl.bindTexture(gl.TEXTURE_2D, this.tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, 2, 2, 0, gl.RGB, gl.UNSIGNED_BYTE, new Uint8Array(12));
    this.uQ = gl.getUniformLocation(prog, "q")!; this.uT = gl.getUniformLocation(prog, "t")!; this.uTint = gl.getUniformLocation(prog, "tint")!;
    gl.uniform1i(gl.getUniformLocation(prog, "tex"), 0);
    this.loop();
  }

  private loop = () => {
    this.raf = requestAnimationFrame(this.loop);
    const gl = this.gl, s = this.source;
    const el = s?.element;
    const ready = el instanceof HTMLVideoElement ? el.readyState >= 2 && el.videoWidth > 0 : !!el;
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    gl.bindTexture(gl.TEXTURE_2D, this.tex);
    if (ready && el) { try { gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, el); } catch { /* frame not ready */ } }
    gl.uniform1f(this.uQ, ready ? (s?.quality ?? 1) : 0);
    gl.uniform1f(this.uT, (performance.now() - this.t0) / 1000);
    gl.uniform1f(this.uTint, s?.tint ?? 0.2);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  };

  destroy() { cancelAnimationFrame(this.raf); this.gl.getExtension("WEBGL_lose_context")?.loseContext(); }
}

/** Procedural crew portrait for opt-outs: drawn once, then fed through the same shader. */
export function portraitCanvas(name: string, seed: string): HTMLCanvasElement {
  const c = document.createElement("canvas"); c.width = 160; c.height = 120;
  const g = c.getContext("2d")!;
  let h = 0; for (const ch of seed) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  const r = (n: number) => ((h = (h * 1664525 + 1013904223) >>> 0) % n);
  g.fillStyle = "#1a1f1a"; g.fillRect(0, 0, 160, 120);
  g.fillStyle = "#2c352c"; for (let i = 0; i < 6; i++) g.fillRect(r(160), r(120), 20 + r(40), 2);
  g.fillStyle = "#9aa89a";
  g.beginPath(); g.ellipse(80, 54 + r(6), 22 + r(6), 28 + r(6), 0, 0, Math.PI * 2); g.fill(); // head
  g.beginPath(); g.ellipse(80, 125, 48 + r(10), 34, 0, Math.PI, 0); g.fill();            // shoulders
  g.fillStyle = "#1a1f1a"; g.fillRect(64 + r(4), 48 + r(4), 12, 4); g.fillRect(86 + r(4), 48 + r(4), 12, 4); // visor band
  g.fillStyle = "#c8d8c8"; g.font = "bold 11px ui-monospace, monospace"; g.textAlign = "center";
  g.fillText(name.toUpperCase().slice(0, 12), 80, 112);
  g.fillStyle = "#5a6a5a"; g.font = "8px ui-monospace, monospace"; g.fillText("CAMERA INOPERATIVE", 80, 12);
  return c;
}
